/**
 * Locale-aware date / time formatters used across the booking flow.
 *
 * `Intl.DateTimeFormat` in browsers and Node sometimes lacks the Armenian
 * CLDR data and falls back to English (rendering "Mon, Jun 8" instead of
 * "Երկ, 8 Հնս"). To guarantee localized output we drive everything from
 * explicit translation keys defined in `booking.datetime.weekdays` /
 * `booking.datetime.months`, picking the right ordering by language.
 */

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

function pad(n) {
  return String(n).padStart(2, "0");
}

/** "YYYY-MM-DD" key for grouping slots by calendar day. */
export function dayKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

/**
 * Parse the wire format "YYYY-MM-DD HH:mm:ss" used by mobile-ticket
 * endpoints into a JS Date in the user's local timezone.
 */
export function parseSlotDate(value) {
  if (!value) return null;
  const iso = String(value).replace(" ", "T");
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Երկ" / "Mon" — short weekday in the active locale. */
export function formatWeekday(date, t) {
  const key = WEEKDAY_KEYS[date.getDay()];
  return t(`booking.datetime.weekdays.${key}`);
}

/** "Հնս" / "Jun" — short month name in the active locale. */
export function formatMonth(date, t) {
  const key = MONTH_KEYS[date.getMonth()];
  return t(`booking.datetime.months.${key}`);
}

/**
 * Pretty day label, optionally with relative shortcuts ("Today" / "Tomorrow")
 * when `opts.relative` is true. Examples:
 *   - hy: "Երկ, 8 Հնս"
 *   - en: "Mon, Jun 8"
 */
export function formatDayLabel(date, lang, t, opts = {}) {
  if (opts.relative) {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (dayKey(date) === dayKey(today)) {
      return t("booking.datetime.groupTitle.today");
    }
    if (dayKey(date) === dayKey(tomorrow)) {
      return t("booking.datetime.groupTitle.tomorrow");
    }
  }
  const weekday = formatWeekday(date, t);
  const month = formatMonth(date, t);
  const day = date.getDate();
  // Armenian conventionally uses "DD MonthName"; English uses "MonthName DD".
  if (lang === "hy") return `${weekday}, ${day} ${month}`;
  return `${weekday}, ${month} ${day}`;
}

/** "14:30" — 24-hour clock, locale-independent. */
export function formatClockTime(date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Pretty day + time for confirmations / ticket cards: "Երկ, 8 Հնս, 14:30". */
export function formatDayTimeLabel(date, lang, t) {
  return `${formatDayLabel(date, lang, t)}, ${formatClockTime(date)}`;
}
